// src/services/api/admin.withdrawal.service.js

// Import the configured apiClient
import { apiClient } from "../../config/api.config";

/**
 * Handles API errors consistently for admin withdrawal service
 * @param {Object} error - The error object caught from API calls
 * @param {string} defaultMessage - Default error message to use
 * @returns {Object} - Standardized error response object
 */
const handleApiError = (
  error,
  defaultMessage = "An unexpected error occurred."
) => {
  let message = defaultMessage;
  let statusCode = 500; // Default internal server error

  if (error.response && error.response.data) {
    message =
      error.response.data.message ||
      error.response.data.error ||
      defaultMessage;
    statusCode = error.response.status || statusCode;
  } else if (error.message) {
    message = error.message;
  }

  return {
    success: false,
    error: message,
    statusCode: statusCode,
  };
};

const withdrawalService = {
  /**
   * Fetches all withdrawal requests for admin.
   * @param {Object} params - Query parameters for filtering and pagination.
   * @param {string} [params.status] - Filter by status (e.g., 'PENDING', 'APPROVED', 'REJECTED').
   * @param {number} [params.page=1] - Current page number.
   * @param {number} [params.limit=20] - Number of items per page.
   * @param {string} [params.sortBy='requestedAt'] - Field to sort by.
   * @param {string} [params.sortOrder='desc'] - Sort order ('asc' or 'desc').
   * @returns {Promise<Object>} - Withdrawal requests data and pagination info, or error.
   */
  getAllWithdrawalRequests: async (params = {}) => {
    const {
      page = 1,
      limit = 20,
      sortBy = "requestedAt",
      sortOrder = "desc",
      status = "",
    } = params;

    // Basic validation for pagination and sorting parameters
    if (isNaN(page) || page < 1) {
      return { success: false, error: "Please provide a valid page number." };
    }
    if (isNaN(limit) || limit < 1 || limit > 100) {
      return { success: false, error: "Please provide a valid limit (1-100)." };
    }

    const queryParams = new URLSearchParams({
      page,
      limit,
      sortBy,
      sortOrder,
    });
    if (status) {
      queryParams.append("status", status);
    }

    try {
      const response = await apiClient.get(
        `transactions/admin/withdrawal-requests/all?${queryParams.toString()}`
      );
      return {
        success: true,
        data: response.data, // Return only the data payload
      };
    } catch (error) {
      return handleApiError(error, "Failed to fetch all withdrawal requests");
    }
  },

  /**
   * Fetches only pending withdrawal requests for admin.
   * @param {Object} params - Query parameters for pagination and sorting.
   * @param {number} [params.page=1] - Current page number.
   * @param {number} [params.limit=20] - Number of items per page.
   * @param {string} [params.sortBy='requestedAt'] - Field to sort by.
   * @param {string} [params.sortOrder='desc'] - Sort order ('asc' or 'desc').
   * @returns {Promise<Object>} - Pending withdrawal requests data and pagination info, or error.
   */
  getPendingWithdrawalRequests: async (params = {}) => {
    const {
      page = 1,
      limit = 20,
      sortBy = "requestedAt",
      sortOrder = "desc",
    } = params;

    // Basic validation for pagination and sorting parameters
    if (isNaN(page) || page < 1) {
      return { success: false, error: "Please provide a valid page number." };
    }
    if (isNaN(limit) || limit < 1 || limit > 100) {
      return { success: false, error: "Please provide a valid limit (1-100)." };
    }

    const queryParams = new URLSearchParams({
      page,
      limit,
      sortBy,
      sortOrder,
    });

    try {
      const response = await apiClient.get(
        `/transactions/admin/withdrawal-requests/pending?${queryParams.toString()}`
      );
      return {
        success: true,
        data: response.data, // Return only the data payload
      };
    } catch (error) {
      return handleApiError(
        error,
        "Failed to fetch pending withdrawal requests"
      );
    }
  },

  /**
   * Fetches statistical data about withdrawal requests.
   * @returns {Promise<Object>} - Withdrawal statistics, or error.
   */
  getWithdrawalStatistics: async () => {
    try {
      const response = await apiClient.get(
        `/transactions/admin/withdrawal-requests/statistics`
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, "Failed to fetch withdrawal statistics");
    }
  },

  /**
   * Fetches a specific withdrawal request by its ID.
   * @param {string} requestId - The ID of the withdrawal request.
   * @returns {Promise<Object>} - Details of the specific withdrawal request, or error.
   */
  getWithdrawalRequestById: async (requestId) => {
    if (
      !requestId ||
      typeof requestId !== "string" ||
      requestId.trim() === ""
    ) {
      return {
        success: false,
        error: "Request ID is required and must be a valid string.",
      };
    }
    try {
      const response = await apiClient.get(
        `/transactions/admin/withdrawal-requests/${requestId}`
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(
        error,
        `Failed to fetch withdrawal request ${requestId}`
      );
    }
  },

  /**
   * Approves a withdrawal request.
   * @param {string} requestId - The ID of the withdrawal request to approve.
   * @param {string} [adminNotes=''] - Optional notes from the admin.
   * @returns {Promise<Object>} - Confirmation of approval, or error.
   */
  approveWithdrawalRequest: async (requestId, adminNotes = "") => {
    if (
      !requestId ||
      typeof requestId !== "string" ||
      requestId.trim() === ""
    ) {
      return {
        success: false,
        error: "Request ID is required to approve a request.",
      };
    }
    try {
      const response = await apiClient.patch(
        `/transactions/admin/withdrawal-requests/${requestId}/approve`,
        { adminNotes }
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, "Failed to approve withdrawal request");
    }
  },

  /**
   * Rejects a withdrawal request.
   * @param {string} requestId - The ID of the withdrawal request to reject.
   * @param {string} rejectionReason - The reason for rejecting the request.
   * @returns {Promise<Object>} - Confirmation of rejection, or error.
   */
  rejectWithdrawalRequest: async (requestId, rejectionReason) => {
    if (
      !requestId ||
      typeof requestId !== "string" ||
      requestId.trim() === ""
    ) {
      return {
        success: false,
        error: "Request ID is required to reject a request.",
      };
    }
    if (
      !rejectionReason ||
      typeof rejectionReason !== "string" ||
      rejectionReason.trim() === ""
    ) {
      return { success: false, error: "Rejection reason is required." };
    }
    try {
      const response = await apiClient.patch(
        `/transactions/admin/withdrawal-requests/${requestId}/reject`,
        { rejectionReason }
      );
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return handleApiError(error, "Failed to reject withdrawal request");
    }
  },
  /**
   * Fetches a specific withdrawal request by ID (admin view).
   * @param {string} requestId - The ID of the withdrawal request.
   * @returns {Promise<Object>} - Details of the specific withdrawal request, or error.
   */
  getWithdrawalRequestById: async (requestId) => {
    if (
      !requestId ||
      typeof requestId !== "string" ||
      requestId.trim() === ""
    ) {
      return {
        success: false,
        error: "Request ID is required and must be a valid string.",
      };
    }
    try {
      const response = await apiClient.get(
        `/transactions/admin/withdrawal-requests/${requestId}`
      );

      // Handle nested response structure and flatten it
      const responseData = response.data;

      if (responseData?.success && responseData?.data) {
        return {
          success: true,
          data: responseData.data, // Extract the actual data from nested structure
        };
      } else {
        return {
          success: false,
          error: responseData?.error || "Invalid response format",
        };
      }
    } catch (error) {
      return handleApiError(
        error,
        `Failed to fetch withdrawal request ${requestId}`
      );
    }
  },
};

export default withdrawalService;
